import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { $axios } from "@bloglovin/vue-framework";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { defineStore } from "pinia";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import useDynamicComponentStore from "%/dynamic-component-editor/stores/dynamic-component-store";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import Publication from "%/dynamic-component-editor/classes/publication";

export const useConfigurationPanelStore = defineStore("configuration-panel-store", {
  state: () => ({
    errorCreatingNewDynamicComponent: false,
    errorCreatingNewDynamicPage: false,
    errorDuplicatingDynamicComponent: false,
    errorDeletingDynamicComponent: false,
    isLoadingCreateNewDynamicComponent: false,
    isLoadingCreateNewDynamicPage: false,
    showCreateDynamicComponentModal: false,
    showCreateDynamicPageModal: false,
    createDynamicPageModalHasCustomSlug: false,
    newDynamicComponentLayout: {},
    dynamicComponentToBeDeleted: null,
    dynamicComponentDeleteModalOpen: false,
  }),
  getters: {
    publicationId() {
      return useConfigurationStore().publicationId;
    },
    apiV1() {
      return useGeneralStore().links.api;
    },
    webLink() {
      return useGeneralStore().links.web;
    },
    hasError() {
      if (this.errorDuplicatingDynamicComponent) {
        return "Error duplicating dynamic component";
      }

      if (this.errorCreatingNewDynamicComponent) {
        return "Error creating new dynamic component";
      }

      if (this.errorCreatingNewDynamicPage) {
        return "Error creating new dynamic page";
      }

      if (this.errorDeletingDynamicComponent) {
        return "Error deleting dynamic component";
      }

      return "";
    },
  },
  actions: {
    async createDynamicComponentWithoutTemplate(componentName, rootOptions = {}, successCallback = () => {
    }) {
      if (this.isLoadingCreateNewDynamicComponent) {
        return;
      }

      const dynamicComponentInstance = DynamicComponent.get(
        useDynamicComponentEditorStore().dynamicComponentId,
        useFanPublicationStore().publicationId,
      );
      const sanitizedLayout = useDynamicComponentEditorStore().sanitizeConfiguration(
        dynamicComponentInstance.getElementConfigInOldFormat(
          this.newDynamicComponentLayout.identifier,
          this.newDynamicComponentLayout,
        ),
      );

      const customComponentRef = await import("~/dynamic-components/standard-components/custom-component/custom-component");
      let invalidElementTypes = useDynamicComponentEditorStore().validateCloningElements(sanitizedLayout, customComponentRef.default);
      if (invalidElementTypes.length) {
        useDynamicComponentEditorStore().toggleInvalidCloneModal(
          "You are attempting to create a component with elements not supported on custom components:",
          invalidElementTypes,
        );
        return;
      }

      let dynamicComponentId = null;
      $axios.post(`${this.apiV1}/dynamic-component/layout/${this.publicationId}/custom-component/create`, {
        dynamic_component_type: "custom-component",
        dynamic_component_class: "CustomComponent",
        dynamic_component_name: componentName,
        layout: sanitizedLayout,
        root_options: {
          ...rootOptions,
          "display-name": componentName,
        },
      }).then(response => {
        const responseData = response.data;
        dynamicComponentId = responseData.dynamic_component_id;

        const dynamicComponentForConfiguration = {
          ["dynamic_component_id"]: dynamicComponentId,
          ["dynamic_component_version_id"]: responseData.dynamic_component_version_id,
          ["dynamic_component_type"]: "custom-component",
          ["dynamic_component_class"]: "CustomComponent",
          ["dynamic_component_name"]: componentName,
          ["component-name"]: componentName,
        };
        useConfigurationStore().addDynamicComponentToConfiguration(
          dynamicComponentId,
          dynamicComponentForConfiguration,
        );
        useDynamicComponentStore().dynamicComponents[dynamicComponentId] = {
          ...responseData,
        };

        successCallback(dynamicComponentId);
      }).catch(() => {
        this.errorCreatingNewDynamicComponent = true;
      }).finally(() => {
        this.newDynamicComponentLayout = {};
        this.isLoadingCreateNewDynamicComponent = false;
      });
      this.isLoadingCreateNewDynamicComponent = true;
    },
    findOriginalTemplateTypeConfig(templateType) {
      let templateWithType = Object.values(useDynamicComponentStore().dynamicComponents).find(component => {
        const rootOptions = component.rootOptions;
        if (rootOptions["template-type"] === templateType && rootOptions["original-template-type"]) {
          return true;
        }
      });
      if (!templateWithType) {
        templateWithType = Object.values(useDynamicComponentStore().parentDynamicComponents).find(component => {
          const rootOptions = component.rootOptions;
          if (rootOptions["template-type"] === templateType && rootOptions["original-template-type"]) {
            return true;
          }
        });
      }

      return templateWithType;
    },
    calculateRootOptionsForCreation(details) {
      let rootOptions = {};

      let originalTemplateWithType;
      let templateWithTypeVersion;
      let templateWithTypeId;
      let isLocal = true;
      if (details.templateType) {
        originalTemplateWithType = this.findOriginalTemplateTypeConfig(details.templateType);

        if (originalTemplateWithType) {
          templateWithTypeVersion = originalTemplateWithType.dynamic_component_version_id;
          templateWithTypeId = originalTemplateWithType.dynamic_component_id;
          isLocal = originalTemplateWithType.rootOptions?.["local-template-type"] ?? false;
        } else {
          isLocal = true;
        }
      }

      if (details.templateType) {
        rootOptions = {
          "template-type": details.templateType,
          "local-template-type": isLocal,
          "original-template-type": !originalTemplateWithType,
          "original-template-type-id": templateWithTypeId,
          "original-template-type-version-id": templateWithTypeVersion,
        };
      }

      return rootOptions;
    },
    createDynamicComponentFromIdentifier(identifier) {
      const componentConfig = useDynamicComponentEditorStore().getRelatedDynamicComponent(identifier);
      this.createDynamicComponent({
        name: componentConfig.rootOptions["component-name"],
        templateType: componentConfig.rootOptions["template-type"],
        template: componentConfig.dynamic_component_id,
      });
    },
    createDynamicComponent(details) {
      if (this.isLoadingCreateNewDynamicComponent) {
        return;
      }
      this.isLoadingCreateNewDynamicComponent = true;

      const rootOptions = this.calculateRootOptionsForCreation(details);

      $axios.post(
        `${this.apiV1}/dynamic-component/${this.publicationId}/custom-component/create`,
        {
          dynamic_component_type: "custom-component",
          dynamic_component_class: "CustomComponent",
          dynamic_component_name: details.name,
          template: details.template,
          root_options: {
            ...rootOptions,
            "display-name": details.name,
          },
        },
      ).then(response => {
        window.location.href = this.webLink + `/dynamic-component/${response.data.configuration.dynamic_component_id}`;
      }).catch(() => {
        this.errorCreatingNewDynamicComponent = true;
      }).finally(() => {
        this.isLoadingCreateNewDynamicComponent = false;
      });
    },
    createDynamicPage(details, successCallback = null) {
      if (this.isLoadingCreateNewDynamicPage) {
        return;
      }
      this.isLoadingCreateNewDynamicPage = true;
      $axios.post(
        `${this.apiV1}/pages/publication/${this.publicationId}/create`,
        {
          page_title: details.name,
          page_slug: details.slug,
          dynamic_component_type: "custom-page",
          dynamic_component_class: "PublicationPage",
          dynamic_component_name: details.name,
          template: details.template,
        },
      ).then(response => {
        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        if (successCallback) {
          successCallback(response.data);
        } else {
          window.location.href = `${this.webLink}/page/${response.data.dynamicComponentId}`;
        }
      }).catch(() => {
        this.errorCreatingNewDynamicPage = true;
      }).finally(() => {
        this.isLoadingCreateNewDynamicPage = false;
      });
    },
    openDynamicComponentDeleteModal(dynamicComponent) {
      this.dynamicComponentToBeDeleted = dynamicComponent;
      this.deleteDynamicComponentModalOpen = true;
    },
    deleteDynamicComponent(dynamicComponent) {
      const dynamicComponentId = dynamicComponent.dynamic_component_id;

      this.isLoadingDeleteDynamicComponent = true;
      $axios.post(this.apiV1 + "/dynamic-component/delete", {
        dynamic_component_id: dynamicComponentId,
      }).then(() => {
        this.dynamicComponentToBeDeleted = null;
        this.deleteDynamicComponentModalOpen = false;
        delete useConfigurationStore().$state.$_configuration.dynamicComponents[dynamicComponentId];
      }).catch(() => {
        this.errorDeletingDynamicComponent = true;
      }).finally(() => {
        this.isLoadingDeleteDynamicComponent = false;
      });
    },
  },
});
